import addHighContrastStyles from '@/lib/styling/addHighContrastStyles';
import stripProps from '@/lib/utils/stripProps';
import styled from '@emotion/styled';
import { Close as DeleteIcon } from '@mui/icons-material';
import {
  alpha,
  ButtonBase,
  type ButtonBaseProps,
  darken,
  lighten,
} from '@mui/material';
import { common } from '@mui/material/colors';

type ButtonWrapperProps = {
  useErrorMode?: boolean;
};

const ButtonWrapper = styled('div')<ButtonWrapperProps>(
  ({ theme, useErrorMode }) => ({
    borderLeftStyle: 'solid',
    borderLeftWidth: 2,
    borderLeftColor: useErrorMode
      ? theme.isDark
        ? darken(theme.palette.error.main, 0.2)
        : lighten(theme.palette.error.light, 0.6)
      : theme.palette.action.hover,
    display: 'inline-flex',
  }),
  addHighContrastStyles(({ theme }) => ({
    borderLeft: `2px solid ${theme.palette.divider}`,
  })),
);

type StyledButtonProps = ButtonBaseProps & {
  useErrorMode?: boolean;
};

const StyledButton = styled(
  stripProps(ButtonBase, 'useErrorMode'),
)<StyledButtonProps>(
  ({ theme, useErrorMode }) => ({
    paddingLeft: 6,
    paddingRight: 6,
    height: 40,
    color: useErrorMode
      ? theme.isDark
        ? theme.palette.error.light
        : theme.palette.error.dark
      : alpha(theme.palette.text.secondary, 0.4),
    transition: theme.transitions.create('background-color'),
    '.MuiSvgIcon-root': {
      width: 18,
      height: 18,
      transition: theme.transitions.create('color'),
    },
    ':hover, :focus': {
      backgroundColor: useErrorMode
        ? theme.isDark
          ? darken(theme.palette.error.main, 0.2)
          : lighten(theme.palette.error.light, 0.6)
        : theme.palette.action.hover,
      '.MuiSvgIcon-root': {
        color: useErrorMode
          ? theme.isDark
            ? lighten(theme.palette.error.light, 0.3)
            : undefined
          : theme.isDark
            ? theme.palette.error.light
            : theme.palette.error.main,
      },
    },
    ':focus': {
      backgroundColor: useErrorMode ? undefined : theme.palette.action.selected,
    },
  }),
  addHighContrastStyles(({ theme, useErrorMode }) => ({
    color: useErrorMode && theme.isDark ? common.black : theme.palette.divider,
    ':hover, :focus': {
      backgroundColor: theme.palette.divider,
      '.MuiSvgIcon-root': {
        color: theme.palette.background.default,
      },
    },
  })),
);

type FileChipRemoveButtonProps = Omit<ButtonBaseProps, 'children'> & {
  useErrorMode?: boolean;
};

const FileChipRemoveButton = ({
  useErrorMode,
  ...props
}: FileChipRemoveButtonProps) => {
  return (
    <ButtonWrapper useErrorMode={useErrorMode}>
      <StyledButton useErrorMode={useErrorMode} {...props}>
        <DeleteIcon />
      </StyledButton>
    </ButtonWrapper>
  );
};

export default FileChipRemoveButton;
